import React, { useEffect, useState, useContext } from 'react';
import {
  Container, Typography, Paper, Box, TextField, Button, IconButton, List, ListItem, ListItemText, ListItemSecondaryAction, Dialog, DialogTitle, DialogContent, DialogActions, Alert
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Save as SaveIcon, Close as CloseIcon } from '@mui/icons-material';
import { lambdaGroupsApi } from '../services/api';
import { UserRoleContext } from './Layout';

const ProductGroupsAdmin: React.FC = () => {
  const { role } = useContext(UserRoleContext);
  const [groups, setGroups] = useState<{ _id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newGroup, setNewGroup] = useState('');
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [deleteIdx, setDeleteIdx] = useState<number | null>(null);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const res = await lambdaGroupsApi.getAll();
      setGroups(res);
    } catch (e) {
      setError('Greška pri dohvatanju grupa');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGroups(); }, []);

  if (role !== 'admin' && role !== 'master_admin') {
    return <Container maxWidth="md"><Alert severity="error">Samo admin ili master admin može da pristupi ovoj stranici.</Alert></Container>;
  }

  const handleAdd = async () => {
    if (!newGroup.trim()) return;
    setLoading(true);
    try {
      await lambdaGroupsApi.create({ name: newGroup.trim() });
      setNewGroup('');
      fetchGroups();
      setSuccess('Grupa dodata');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Greška pri dodavanju');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (idx: number) => {
    const group = groups[idx];
    if (!editValue.trim()) return;
    setLoading(true);
    try {
      await lambdaGroupsApi.update(group._id, { name: editValue.trim() });
      setEditIdx(null);
      setEditValue('');
      fetchGroups();
      setSuccess('Grupa izmenjena');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Greška pri izmeni');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (idx: number) => {
    const group = groups[idx];
    setLoading(true);
    try {
      await lambdaGroupsApi.delete(group._id);
      setDeleteIdx(null);
      fetchGroups();
      setSuccess('Grupa obrisana');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Greška pri brisanju');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>Administracija grupa proizvoda</Typography>
      {error && <Box sx={{ mb: 2 }}><Typography color="error">{error}</Typography></Box>}
      {success && <Box sx={{ mb: 2 }}><Typography color="success.main">{success}</Typography></Box>}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', mb: 2 }}>
          <TextField
            label="Nova grupa"
            value={newGroup}
            onChange={e => setNewGroup(e.target.value)}
            fullWidth
            size="small"
            sx={{ mr: 2 }}
            onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} disabled={loading || !newGroup.trim()}>
            Dodaj
          </Button>
        </Box>
        <List>
          {groups.map((group, idx) => (
            <ListItem key={group._id} divider>
              {editIdx === idx ? (
                <TextField
                  value={editValue}
                  onChange={e => setEditValue(e.target.value)}
                  size="small"
                  sx={{ mr: 2 }}
                  onKeyDown={e => { if (e.key === 'Enter') handleEdit(idx); }}
                  autoFocus
                />
              ) : (
                <ListItemText primary={group.name} />
              )}
              <ListItemSecondaryAction>
                {editIdx === idx ? (
                  <>
                    <IconButton edge="end" onClick={() => handleEdit(idx)} disabled={loading || !editValue.trim()}>
                      <SaveIcon />
                    </IconButton>
                    <IconButton edge="end" onClick={() => { setEditIdx(null); setEditValue(''); }}>
                      <CloseIcon />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <IconButton edge="end" onClick={() => { setEditIdx(idx); setEditValue(group.name); }}>
                      <EditIcon />
                    </IconButton>
                    <IconButton edge="end" color="error" onClick={() => setDeleteIdx(idx)}>
                      <DeleteIcon />
                    </IconButton>
                  </>
                )}
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Paper>
      <Dialog open={deleteIdx !== null} onClose={() => setDeleteIdx(null)}>
        <DialogTitle>Obrisati grupu?</DialogTitle>
        <DialogContent>
          <Typography>Da li ste sigurni da želite da obrišete ovu grupu?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteIdx(null)}>Otkaži</Button>
          <Button color="error" variant="contained" onClick={() => deleteIdx !== null && handleDelete(deleteIdx)}>Obriši</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProductGroupsAdmin; 